import React, { useState, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Bell, X, Search, Check } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Modal de création de reminder — utilise @radix-ui/react-dialog pour
 * gérer correctement le focus trap même lorsqu'un Sheet est ouvert.
 *
 * Props :
 *   open            : bool
 *   onClose         : () => void
 *   onCreated       : (reminder) => void  (optionnel)
 *   typeDocument    : string  ex. 'vente' | 'location' | 'reparation' | 'achat' | 'entreprise'
 *   documentId      : string  (ID technique)
 *   numeroDocument  : string  (numéro lisible affiché)
 */
export default function ReminderModal({ open, onClose, onCreated, typeDocument, documentId, numeroDocument }) {
  const { user } = useAuth();

  // Form state
  const [texte,        setTexte]        = useState('');
  const [dateEcheance, setDateEcheance] = useState('');
  const [assignedUser, setAssignedUser] = useState(null);
  const [saving,       setSaving]       = useState(false);

  // User search
  const [users,        setUsers]        = useState([]);
  const [userSearch,   setUserSearch]   = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchRef = useRef(null);

  // Reset form on open
  useEffect(() => {
    if (!open) return;
    setTexte('');
    setDateEcheance('');
    setUserSearch('');
    setAssignedUser(user ? { user_id: user.user_id, name: user.name || user.email } : null);
    setDropdownOpen(false);
    api.getUsers().then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setUsers(list.filter(u => u.actif !== false));
    }).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredUsers = users.filter(u =>
    !userSearch.trim() ||
    (u.name || u.email || '').toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!assignedUser) return toast.error('Sélectionnez un utilisateur');
    if (!dateEcheance)  return toast.error('Sélectionnez une date');
    if (!texte.trim())  return toast.error('Saisissez un texte');
    setSaving(true);
    try {
      const r = await api.createReminder({
        assigned_to_id:  assignedUser.user_id,
        assigned_to_nom: assignedUser.name,
        date_echeance:   dateEcheance,
        texte:           texte.trim(),
        type_document:   typeDocument   || null,
        document_id:     documentId     || null,
        numero_document: numeroDocument || null,
      });
      toast.success('Rappel créé');
      onCreated?.(r.data);
      onClose();
    } catch {
      toast.error('Erreur lors de la création du rappel');
    } finally {
      setSaving(false);
    }
  };

  const LABELS = {
    vente:      'Vente',
    location:   'Location',
    reparation: 'Réparation',
    achat:      'Achat',
    entreprise: 'Entreprise',
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        {/* Overlay */}
        <Dialog.Overlay
          className="fixed inset-0"
          style={{ background: 'rgba(0,0,0,0.45)', zIndex: 10000 }}
        />
        {/* Contenu */}
        <Dialog.Content
          className="fixed left-1/2 top-1/2 w-full max-w-md mx-auto rounded-xl shadow-2xl focus:outline-none"
          style={{
            transform: 'translate(-50%, -50%)',
            background: 'var(--bg-card, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            zIndex: 10001,
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--border, #e5e7eb)' }}>
            <div className="flex items-center gap-2">
              <Bell size={18} color="#8B5CF6" />
              <Dialog.Title className="font-semibold text-sm" style={{ color: 'var(--text)' }}>
                Nouveau rappel
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="p-1 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-muted)' }}>
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Contexte document (lecture seule) */}
            {typeDocument && (
              <div className="rounded-lg px-3 py-2 text-xs" style={{ background: '#F3E8FF', color: '#7C3AED' }}>
                <span className="font-bold">{LABELS[typeDocument] || typeDocument}</span>
                {numeroDocument && <span className="ml-2 opacity-80">· {numeroDocument}</span>}
              </div>
            )}

            {/* Assigné à */}
            <div className="relative" ref={searchRef}>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                Assigné à
              </label>
              <button
                type="button"
                onClick={() => setDropdownOpen(o => !o)}
                className="w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm text-left"
                style={{ border: '1px solid var(--border, #e5e7eb)', background: 'var(--bg-input, #fff)', color: 'var(--text)' }}
              >
                <span>{assignedUser ? (assignedUser.name || assignedUser.user_id) : 'Sélectionner…'}</span>
                <Search size={13} style={{ color: 'var(--text-secondary, #9ca3af)' }} />
              </button>
              {dropdownOpen && (
                <div className="absolute z-[10002] mt-1 w-full rounded-lg shadow-lg overflow-hidden" style={{ background: 'var(--bg-card, #fff)', border: '1px solid var(--border, #e5e7eb)' }}>
                  <div className="p-2">
                    <input
                      autoFocus
                      className="w-full text-sm px-2 py-1 rounded outline-none"
                      style={{ border: '1px solid var(--border, #e5e7eb)', color: 'var(--text)' }}
                      placeholder="Rechercher…"
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                    />
                  </div>
                  <ul className="max-h-40 overflow-y-auto">
                    {filteredUsers.length === 0 && (
                      <li className="px-3 py-2 text-xs" style={{ color: 'var(--text-secondary, #9ca3af)' }}>Aucun utilisateur</li>
                    )}
                    {filteredUsers.map(u => (
                      <li
                        key={u.user_id}
                        className="flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-gray-50"
                        style={{ color: 'var(--text)' }}
                        onClick={() => { setAssignedUser({ user_id: u.user_id, name: u.name || u.email }); setDropdownOpen(false); setUserSearch(''); }}
                      >
                        {assignedUser?.user_id === u.user_id && <Check size={12} style={{ color: '#8B5CF6' }} />}
                        <span>{u.name || u.email}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Date d'échéance */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                Date d'échéance
              </label>
              <input
                type="date"
                required
                value={dateEcheance}
                onChange={e => setDateEcheance(e.target.value)}
                className="w-full text-sm px-3 py-2 rounded-lg"
                style={{ border: '1px solid var(--border, #e5e7eb)', background: 'var(--bg-input, #fff)', color: 'var(--text)' }}
              />
            </div>

            {/* Texte */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                Note
              </label>
              <textarea
                required
                rows={3}
                value={texte}
                onChange={e => setTexte(e.target.value)}
                placeholder="Rappel à faire…"
                className="w-full text-sm px-3 py-2 rounded-lg resize-none"
                style={{ border: '1px solid var(--border, #e5e7eb)', background: 'var(--bg-input, #fff)', color: 'var(--text)' }}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ background: 'var(--bg-muted, #f3f4f6)', color: 'var(--text-secondary, #6b7280)' }}
                >
                  Annuler
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={saving}
                data-testid="reminder-submit-btn"
                className="flex-1 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                style={{ background: '#8B5CF6', opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Enregistrement…' : 'Créer le rappel'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
